<template>
  <PortalLayout section="Prepare">
    <div v-if="isPropertyLoading || isTrainingProgramsLoading"><Loading /></div>
    <div v-else class="mt-4">
      <div v-if="trainingPrograms && trainingPrograms.length > 0">
        <AddButton @click="addClick">Add Training Program</AddButton>
        <AddButton class="sm:float-right" @click="importPrograms">
          Import Programs from Portal
        </AddButton>
        <div
          v-for="program in trainingPrograms"
          :key="program.trainingProgramId"
          class="p-4 pt-2 my-2 border-t cursor-pointer"
          :class="program.isActive ? 'bg-gray-100' : 'bg-gray-400'"
        >
          <div class="flex">
            <div
              class="hover:bg-blue-200 flex-grow rounded py-1 px-2"
              @click="trainingProgramClick(program)"
            >
              <div class="font-medium">
                {{ program.name }} <span v-if="!program.isActive">(inactive)</span>
              </div>
            </div>
            <EditButton @click="editClick(program)" iconOnly="true" />
          </div>
          <p
            v-for="module in program.trainingModules"
            :key="module.trainingModuleId"
            class="hover:bg-blue-200 rounded cursor-pointer"
            @click="trainingModuleClick(module)"
          >
            <span class="text-sm pl-4">
              {{ module.name }} <span v-if="!module.isActive">(inactive)</span>
            </span>
          </p>
        </div>
      </div>
      <div class="m-2" v-else>
        <span>-- No Training Programs found for this portal --</span>
      </div>
      <AddButton @click="addClick">Add Training Program</AddButton>
      <AddButton
        @click="importPrograms"
        v-if="!trainingPrograms || trainingPrograms.length === 0"
        class="sm:float-right"
      >
        Import Programs from Portal
      </AddButton>

      <!-- Add/Edit Training Program -->
      <EditPopup
        entity-name="Training Program"
        :is-visible="isPopupVisible"
        :is-edit-mode="isEditing"
        :save-error="saveError"
        :delete-enabled="true"
        @close="isPopupVisible = false"
        @save="saveClick"
        @delete="deleteClick"
      >
        <BaseInput label="Name" v-model="programName" :error="errors.programName" />
        <div class="h-3" />
        <BaseCheckbox label="Currently Active" v-model="programIsActive" />
      </EditPopup>

      <!-- Import Program from Portal -->
      <EditPopup
        entity-name="Training Programs from Another Portal"
        :is-visible="isImportPopupVisible"
        :is-edit-mode="false"
        :save-error="saveError"
        :delete-enabled="false"
        save-button-text="Import"
        @close="isImportPopupVisible = false"
        @save="confirmImport"
      >
        <BaseSelect
          v-model="selectedPortal"
          label="Portal"
          :options="portalOptions"
          :error="errorsImport.selectedPortal"
          :default-option="({ name: '--Please Select--', value: 0 } as NameValue)"
        />
        <div class="h-3" />
        <div>Training Programs</div>
        <Loading v-if="isImportPopupLoading" />
        <BaseCheckboxGroup
          v-else-if="programOptions?.length > 0"
          :model-value="selectedProgramOptions"
          :options="programOptions"
          vertical
        />
        <div v-else>None</div>
        <div class="text-red-800" v-if="errorsImport.selectedProgramOptions">
          {{ errorsImport.selectedProgramOptions }}
        </div>
      </EditPopup>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useTrainingStore } from "../../store/trainingStore";
import {
  TrainingProgramDTO,
  TrainingModuleDTO,
  ImportTrainingProgramsFromPortalRequest,
} from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { NameValue } from "../../sharedTypes";

const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();
const router = useRouter();

var trainingPrograms = computed(() => trainingStore.portalTrainingPrograms);
var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var isTrainingProgramsLoading = ref(true);

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  isTrainingProgramsLoading.value = true;
  if (property.value?.portalId) {
    await trainingStore.getTrainingProgramsByPortal(property.value.portalId);
  }
  isTrainingProgramsLoading.value = false;
}

let saveError = ref("");

//---------------------------------------
// #region Add/edit training programs
//---------------------------------------

let isPopupVisible = ref(false);
let isEditing = ref(false);

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    programName: yup.string().required().max(50).label("Name"),
  }),
  initialValues: {
    programName: "",
  },
});

let programId = ref(0);
const { value: programName } = useField<string>("programName");
let programIsActive = ref(true);

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let newProgram = {
    trainingProgramId: programId.value,
    portalId: property.value.portalId,
    name: programName.value,
    isActive: programIsActive.value,
  } as TrainingProgramDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveTrainingProgram(newProgram);
    if (!saveError.value) {
      await trainingStore.getTrainingProgramsByPortal(property.value.portalId);
      isPopupVisible.value = false;
    }
  }
}

let trainingProgramClick = function (program: TrainingProgramDTO) {
  router.push(`prepare/trainingprogram/${program.trainingProgramId}`);
};

let trainingModuleClick = function (program: TrainingModuleDTO) {
  router.push(`prepare/trainingmodule/${program.trainingModuleId}`);
};

let addClick = function () {
  programId.value = 0;
  programName.value = "";
  saveError.value = "";

  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selectedProgram: TrainingProgramDTO) {
  programId.value = selectedProgram.trainingProgramId ?? 0;
  programName.value = selectedProgram.name ?? "";
  programIsActive.value = selectedProgram.isActive;

  saveError.value = "";

  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  if (programId.value) {
    console.log(`Deleting Training Program ${programId.value}`);

    saveError.value = await trainingStore.deleteTrainingProgram(programId.value);
    if (saveError.value) return;

    await trainingStore.getTrainingProgramsByPortal(property.value.portalId);
    isPopupVisible.value = false;
  }
}

//---------------------------------------
// #endregion Add/edit training programs
//---------------------------------------

//---------------------------------------
// #region Import training programs
//---------------------------------------

let portalOptions = computed(() =>
  bldgStore.properties
    .filter((p) => p.portalId !== property.value.portalId)
    .map((p) => ({ name: p.name, value: p.portalId } as NameValue))
);
let programOptions = computed(() =>
  trainingStore.trainingProgramList
    .filter((p) => p.portalId == selectedPortal.value)
    .map((p) => ({ name: p.name, value: p.trainingProgramId } as NameValue))
);

const { handleSubmit: handleSubmitImport, errors: errorsImport } = useForm({
  validationSchema: yup.object({
    selectedPortal: yup
      .number()
      .required()
      .min(1, "Portal is a required field.")
      .label("Portal"),
    selectedProgramOptions: yup
      .array()
      .required()
      .min(1, "Select at least one training program.")
      .label("Training program(s)"),
  }),
  initialValues: {
    selectedPortal: 0,
    selectedProgramOptions: [],
  },
});

let isImportPopupVisible = ref(false);
let isImportPopupLoading = ref(false);
const { value: selectedPortal } = useField<number>("selectedPortal");
const { value: selectedProgramOptions } = useField<number[]>("selectedProgramOptions");

// clear selected programs when changing portal
watch(selectedPortal, () => (selectedProgramOptions.value = [] as number[]));

let confirmImport = handleSubmitImport(onValidSubmitImport, onInvalidSubmit);

async function onValidSubmitImport() {
  let request = {
    destinationPortalId: property.value.portalId,
    trainingProgramIds: selectedProgramOptions.value,
  } as ImportTrainingProgramsFromPortalRequest;

  saveError.value = await trainingStore.importTrainingProgramsFromPortal(request);
  if (saveError.value) return;

  isImportPopupVisible.value = false;
  await trainingStore.getTrainingProgramsByPortal(property.value.portalId);
}

let importPrograms = async function () {
  isImportPopupLoading.value = false;
  selectedPortal.value = 0;

  saveError.value = "";
  isImportPopupVisible.value = true;

  isImportPopupLoading.value = true;
  await trainingStore.getTrainingProgramList();
  isImportPopupLoading.value = false;
};

//---------------------------------------
// #endregion Import training programs
//---------------------------------------

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
}
</script>
